import { useState, useEffect } from 'react'
import { Box, Typography, Tabs, Tab, Alert, AlertTitle, CircularProgress } from '@mui/material'
import {
  Settings, Factory, CalendarMonth, Inventory, ListAlt,
  PeopleAlt, History,
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import PlantConfig from '../components/admin/PlantConfig'
import CapacityConfig from '../components/admin/CapacityConfig'
import DosPolicies from '../components/admin/DosPolicies'
import RationalePresets from '../components/admin/RationalePresets'
import UserManagement from '../components/admin/UserManagement'
import PanelErrorBoundary from '../components/PanelErrorBoundary'
import AuditLog from '../components/admin/AuditLog'

const TABS = [
  { label: 'Plant Config', icon: <Factory fontSize="small" /> },
  { label: 'Capacity Config', icon: <CalendarMonth fontSize="small" /> },
  { label: 'DOS Policies', icon: <Inventory fontSize="small" /> },
  { label: 'Rationale Presets', icon: <ListAlt fontSize="small" /> },
  { label: 'Users', icon: <PeopleAlt fontSize="small" /> },
  { label: 'Audit Log', icon: <History fontSize="small" /> },
]

export default function Admin() {
  const { user } = useAuth()
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [plants, setPlants] = useState(null)
  const [settings, setSettings] = useState(null)
  const [users, setUsers] = useState(null)
  const [audit, setAudit] = useState(null)

  const isAdmin = user?.roles?.includes('Admin') ?? true

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      api.get('/plants').catch(() => null),
      api.get('/settings').catch(() => null),
      api.get('/users').catch(() => null),
      api.get('/audit').catch(() => null),
    ]).then(([plantsData, settingsData, usersData, auditData]) => {
      if (cancelled) return
      if (plantsData) setPlants(plantsData)
      if (settingsData) setSettings(settingsData)
      if (usersData) setUsers(usersData)
      if (auditData) setAudit(auditData)
    }).catch((err) => {
      if (!cancelled) setError(err.message || 'Failed to load admin data')
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => { cancelled = true }
  }, [])

  const handleSaveSettings = async (updatedSettings) => {
    try {
      const result = await api.put('/settings', updatedSettings)
      setSettings(result || updatedSettings)
    } catch (err) {
      setError(err.message || 'Failed to save settings')
    }
  }

  const handleCreateUser = async (userData) => {
    try {
      const created = await api.post('/users', userData)
      // Only what the API stored is shown; no made-up local record.
      if (created) setUsers((prev) => [...(prev || []), created])
      return created
    } catch (err) {
      setError(err.message || 'Failed to create user')
      throw err
    }
  }

  const handleUpdateUser = async (userId, userData) => {
    try {
      const updated = await api.put(`/users/${userId}`, { ...userData, id: userId })
      setUsers((prev) =>
        (prev || []).map((u) => (u.id === userId ? { ...u, ...userData, ...(updated || {}) } : u)),
      )
      return updated
    } catch (err) {
      setError(err.message || 'Failed to update user')
      throw err
    }
  }

  const panels = [
    <PlantConfig key="plants" plants={plants} />,
    <CapacityConfig key="capacity" />,
    <DosPolicies key="dos" settings={settings} onSave={handleSaveSettings} />,
    <RationalePresets key="rationale" settings={settings} onSave={handleSaveSettings} />,
    <UserManagement key="users" users={users} onCreateUser={handleCreateUser} onUpdateUser={handleUpdateUser} />,
    <AuditLog key="audit" entries={audit} />,
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Settings color="primary" />
        <Typography variant="h3">Admin &amp; Configuration</Typography>
      </Box>

      {!isAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Restricted Access</AlertTitle>
          You do not have Admin privileges. Some settings are read-only.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {TABS.map((t) => (
          <Tab key={t.label} label={t.label} icon={t.icon} iconPosition="start" />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        // FAR-85: a crash in one tab stays in that tab.
        <PanelErrorBoundary name={TABS[tab]?.label} resetKey={tab}>
          {panels[tab]}
        </PanelErrorBoundary>
      )}
    </Box>
  )
}
